import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { ArrowUpRight, Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { SITE } from "@/data/site";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/our-work", label: "Our Work" },
  { to: "/about", label: "About" },
];

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      if (window.__lenis === lenis) delete window.__lenis;
    };
  }, []);
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-obsidian-950/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center group">
          <img
            src="/brand/logo.png"
            alt="PHOS Management, Inc."
            className="h-10 lg:h-12 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide uppercase transition-colors ${
                  isActive ? "text-ink-50" : "text-ink-400 hover:text-ink-50"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  <span
                    className={`absolute left-0 -bottom-2 h-px bg-accent transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="header-cta" className="hidden md:inline-flex btn-accent text-[11px] py-3 px-5">
            Request a Quote
            <ArrowUpRight size={16} className="btn-arrow" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 border border-white/15 flex items-center justify-center"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-obsidian-950 border-t border-white/5"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-4xl leading-none uppercase ${isActive ? "text-accent" : "text-ink-50"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-cta" className="btn-accent mt-4 justify-center">
                Request a Quote <ArrowUpRight size={16} className="btn-arrow" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="relative bg-obsidian-950 border-t border-white/5 mt-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-24">
          <div className="lg:col-span-7">
            <p className="eyebrow-accent mb-6">Let&apos;s Build</p>
            <h2 className="font-display text-[14vw] lg:text-[8vw] leading-[0.85] tracking-tight uppercase">
              Something
              <br />
              <span className="text-stroke">Great Together.</span>
            </h2>
            <Link to="/contact" data-testid="footer-cta" className="btn-accent mt-12">
              Request a Quote <ArrowUpRight size={16} className="btn-arrow" />
            </Link>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-10 lg:pt-8">
            <div>
              <p className="overline mb-5">Contact</p>
              <a href={`tel:${SITE.contact.phone}`} className="flex items-start gap-3 text-ink-50 hover:text-accent mb-4">
                <Phone size={14} className="mt-1 text-accent" />
                <span className="text-sm">{SITE.contact.phone}</span>
              </a>
              <a href={`mailto:${SITE.contact.email}`} className="flex items-start gap-3 text-ink-50 hover:text-accent mb-4 break-all">
                <Mail size={14} className="mt-1 text-accent" />
                <span className="text-sm">{SITE.contact.email}</span>
              </a>
              <div className="flex items-start gap-3 text-ink-400">
                <MapPin size={14} className="mt-1 text-accent" />
                <span className="text-sm">{SITE.contact.address}</span>
              </div>
              <p className="text-xs text-ink-600 mt-4">{SITE.contact.hours}</p>
            </div>
            <div>
              <p className="overline mb-5">Quick Links</p>
              <ul className="space-y-3">
                {NAV_LINKS.slice(1).map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-ink-400 hover:text-ink-50">{l.label}</Link>
                  </li>
                ))}
                <li><Link to="/contact" className="text-sm text-ink-400 hover:text-ink-50">Contact</Link></li>
              </ul>
              <p className="overline mt-8 mb-4">Follow</p>
              <ul className="space-y-3">
                {SITE.socials.map((s) => (
                  <li key={s.label}>
                    <a href={s.href} className="text-sm text-ink-400 hover:text-accent">{s.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-white/5 pt-8">
          <div className="text-xs text-ink-600">© {new Date().getFullYear()} PHOS Management Inc. · Licensed & Insured · Southeast Michigan</div>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="text-xs text-ink-400 hover:text-accent uppercase tracking-[0.18em]">Privacy Policy</Link>
            <span className="text-ink-600">·</span>
            <Link to="/terms-of-service" data-testid="footer-terms" className="text-xs text-ink-400 hover:text-accent uppercase tracking-[0.18em]">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  useLenis();
  const { pathname } = useLocation();
  const ref = useRef(null);
  useEffect(() => {
    // Instantly jump to top on route change — bypass Lenis smooth scroll
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Meta Pixel: fire PageView on client-side navigation
    if (typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [pathname]);

  return (
    <div ref={ref} className="relative bg-obsidian-950 text-ink-50 min-h-screen">
      <div className="grain-overlay" aria-hidden />
      <Header />
      <main data-testid="page-main" className="relative z-10">
        {children}
      </main>
      <Footer />
    </div>
  );
}
